import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { countries } from '../data/countries';
import './OfferForm.css';

interface OfferFormData {
  name: string;
  advertiserId: string;
  goalType: 'install' | 'lead' | 'sale' | 'event';
  eventName: string;
  category: 'Incent' | 'Non Incent';
  payout: string;
  payoutCurrency: string;
  dailyClickCap: string;
  monthlyClickCap: string;
  dailyConversionCap: string;
  monthlyConversionCap: string;
  startDate: string;
  endDate: string;
  redirectUrlTemplate: string;
  previewUrl: string;
  description: string;
  restriction: string;
  isActive: boolean;
  isTest: boolean;
  countries: string[];
  deviceType: string;
}

interface OfferEventFormData {
  eventName: string;
  payout: string;
  revenue: string;
  payoutCurrency: string;
  dailyConversionCap: string;
  monthlyConversionCap: string;
  isActive: boolean;
}

const MAX_EVENT_NAME_LENGTH = 191;

const isValidOptionalCap = (value: string) => {
  if (!value.trim()) return true;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0;
};

const AddOffer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState<OfferFormData>({
    name: '',
    advertiserId: '',
    goalType: 'install',
    eventName: '',
    category: 'Non Incent',
    payout: '',
    payoutCurrency: 'USD',
    dailyClickCap: '',
    monthlyClickCap: '',
    dailyConversionCap: '',
    monthlyConversionCap: '',
    startDate: '',
    endDate: '',
    redirectUrlTemplate: '',
    previewUrl: '',
    description: '',
    restriction: '',
    isActive: true,
    isTest: false,
    countries: [],
    deviceType: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [advertisers, setAdvertisers] = useState<any[]>([]);
  const [advertisersLoading, setAdvertisersLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [eventConfigs, setEventConfigs] = useState<OfferEventFormData[]>([]);

  // Multi-select country dropdown state
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>(countries);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchAdvertisers = async () => {
      try {
        const response = await apiService.getAdvertisers();
        setAdvertisers(response.advertisers || []);
      } catch (error) {
        console.error('Failed to fetch advertisers:', error);
        setErrors(prev => ({ ...prev, fetch: 'Failed to load advertisers. Refresh the page and try again.' }));
      } finally {
        setAdvertisersLoading(false);
      }
    };

    fetchAdvertisers();
  }, []);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node) &&
        countryInputRef.current &&
        !countryInputRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle pre-filling form when copying an offer
  useEffect(() => {
    const state = location.state as any;
    if (state?.copyFromOffer) {
      const copiedOffer = state.copyFromOffer;

      setFormData({
        name: copiedOffer.name || '',
        advertiserId: copiedOffer.advertiser_id?.toString() || '',
        goalType: copiedOffer.goal_type || 'install',
        eventName: copiedOffer.event_name || '',
        category: copiedOffer.category || 'Non Incent',
        payout: copiedOffer.payout?.toString() || '',
        payoutCurrency: copiedOffer.payout_currency || 'USD',
        dailyClickCap: copiedOffer.daily_click_cap?.toString() || '',
        monthlyClickCap: copiedOffer.monthly_click_cap?.toString() || '',
        dailyConversionCap: copiedOffer.daily_conversion_cap?.toString() || '',
        monthlyConversionCap: copiedOffer.monthly_conversion_cap?.toString() || '',
        startDate: copiedOffer.start_date ? new Date(copiedOffer.start_date).toISOString().split('T')[0] : '',
        endDate: copiedOffer.end_date ? new Date(copiedOffer.end_date).toISOString().split('T')[0] : '',
        redirectUrlTemplate: copiedOffer.redirect_url_template || '',
        previewUrl: copiedOffer.preview_url || '',
        description: copiedOffer.description || '',
        restriction: copiedOffer.restriction || '',
        isActive: copiedOffer.is_active || false,
        isTest: copiedOffer.is_test || false,
        countries: copiedOffer.countries || [],
        deviceType: copiedOffer.os?.join(', ') || ''
      });

      const copiedEvents = Array.isArray(copiedOffer.events) ? copiedOffer.events : [];
      setEventConfigs(copiedEvents
        .filter((eventConfig: any) => !eventConfig.is_primary)
        .map((eventConfig: any) => ({
          eventName: eventConfig.event_name || '',
          payout: '',
          revenue: '',
          payoutCurrency: eventConfig.payout_currency || copiedOffer.payout_currency || 'USD',
          dailyConversionCap: eventConfig.daily_conversion_cap?.toString() || '',
          monthlyConversionCap: eventConfig.monthly_conversion_cap?.toString() || '',
          isActive: eventConfig.is_active ?? true
        })));

      // Clear the state to prevent re-filling on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const availableDeviceTypes = ['All Devices', 'iPhone', 'iPad', 'Android Phone', 'Android Tablet', 'Desktop', 'Mobile Web'];

  const getPrimaryEventName = () => (
    formData.goalType === 'event' ? formData.eventName.trim() : formData.goalType
  );

  const buildEventsPayload = () => {
    const primaryEvent = {
      event_name: getPrimaryEventName() || 'install',
      event_order: 1,
      is_primary: true,
      payout: formData.payout ? parseFloat(formData.payout) : null,
      revenue: formData.payout ? parseFloat(formData.payout) : null,
      payout_currency: formData.payoutCurrency,
      daily_conversion_cap: formData.dailyConversionCap ? parseInt(formData.dailyConversionCap) : null,
      monthly_conversion_cap: formData.monthlyConversionCap ? parseInt(formData.monthlyConversionCap) : null,
      publisher_postback_enabled: true,
      publisher_postback_percentage: null,
      postback_status: 'start',
      require_prior_event: false,
      is_payable: true,
      is_active: true
    };

    return [
      primaryEvent,
      ...eventConfigs
        .filter(eventConfig => eventConfig.eventName.trim())
        .map((eventConfig, index) => ({
          event_name: eventConfig.eventName.trim(),
          event_order: index + 2,
          is_primary: false,
          payout: null,
          revenue: null,
          payout_currency: eventConfig.payoutCurrency || formData.payoutCurrency,
          daily_conversion_cap: eventConfig.dailyConversionCap ? parseInt(eventConfig.dailyConversionCap) : null,
          monthly_conversion_cap: eventConfig.monthlyConversionCap ? parseInt(eventConfig.monthlyConversionCap) : null,
          publisher_postback_enabled: false,
          publisher_postback_percentage: null,
          postback_status: 'pause',
          require_prior_event: false,
          is_payable: false,
          is_active: eventConfig.isActive
        }))
    ];
  };

  const addEventConfig = () => {
    setEventConfigs(prev => ([
      ...prev,
      {
        eventName: '',
        payout: '',
        revenue: '',
        payoutCurrency: formData.payoutCurrency,
        dailyConversionCap: '',
        monthlyConversionCap: '',
        isActive: true
      }
    ]));
  };

  const updateEventConfig = (index: number, field: keyof OfferEventFormData, value: string | boolean) => {
    setEventConfigs(prev => prev.map((eventConfig, eventIndex) => (
      eventIndex === index ? { ...eventConfig, [field]: value } : eventConfig
    )));
    setErrors(prev => ({
      ...prev,
      [`event_${index}`]: '',
      [`event_daily_cap_${index}`]: '',
      [`event_monthly_cap_${index}`]: '',
      submit: ''
    }));
  };

  const removeEventConfig = (index: number) => {
    setEventConfigs(prev => prev.filter((_, eventIndex) => eventIndex !== index));
    setErrors(prev => Object.fromEntries(
      Object.entries(prev).filter(([key]) => !key.startsWith('event_'))
    ));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    setErrors(prev => ({ ...prev, [name]: '', submit: '' }));
  };

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountrySearch(value);
    setShowCountryDropdown(true);

    // Filter countries based on search term
    const filtered = countries.filter(country =>
      country.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const handleCountryToggle = (country: string) => {
    setFormData(prev => ({
      ...prev,
      countries: prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country]
    }));
    setErrors(prev => ({ ...prev, countries: '', submit: '' }));
  };

  const handleRemoveCountry = (country: string) => {
    setFormData(prev => ({
      ...prev,
      countries: prev.countries.filter(c => c !== country)
    }));
    setErrors(prev => ({ ...prev, countries: '', submit: '' }));
  };

  const handleSelectAllCountries = () => {
    setFormData(prev => ({
      ...prev,
      countries: [...countries]
    }));
    setErrors(prev => ({ ...prev, countries: '', submit: '' }));
  };

  const handleDeselectAllCountries = () => {
    setFormData(prev => ({
      ...prev,
      countries: []
    }));
  };

  const focusFirstInvalidField = () => {
    window.requestAnimationFrame(() => {
      const firstInvalidField = document.querySelector<HTMLElement>('.offer-form .error');
      firstInvalidField?.focus();
      firstInvalidField?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Offer name is required';
    } else if (formData.name.trim().length > MAX_EVENT_NAME_LENGTH) {
      newErrors.name = `Offer name must be ${MAX_EVENT_NAME_LENGTH} characters or fewer`;
    }

    if (!formData.advertiserId) {
      newErrors.advertiserId = 'Advertiser is required';
    }

    if (!formData.payout.trim()) {
      newErrors.payout = 'Payout is required';
    } else if (isNaN(Number(formData.payout)) || Number(formData.payout) <= 0) {
      newErrors.payout = 'Payout must be a positive number';
    } else if (Number(formData.payout) > 999999.9999) {
      newErrors.payout = 'Payout cannot exceed 999999.9999';
    }

    if (!formData.redirectUrlTemplate.trim()) {
      newErrors.redirectUrlTemplate = 'Redirect URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.redirectUrlTemplate)) {
      newErrors.redirectUrlTemplate = 'Redirect URL must be a valid URL';
    }

    if (formData.goalType === 'event' && !formData.eventName.trim()) {
      newErrors.eventName = 'Event name is required for custom events';
    } else if (getPrimaryEventName().length > MAX_EVENT_NAME_LENGTH) {
      newErrors.eventName = `Event name must be ${MAX_EVENT_NAME_LENGTH} characters or fewer`;
    }

    const eventNames = new Set<string>();
    const primaryEventName = getPrimaryEventName();
    if (primaryEventName) eventNames.add(primaryEventName.toLocaleLowerCase('en-US'));
    eventConfigs.forEach((eventConfig, index) => {
      const eventName = eventConfig.eventName.trim();
      const normalizedEventName = eventName.toLocaleLowerCase('en-US');
      if (!eventName) {
        newErrors[`event_${index}`] = 'Event name is required';
      } else if (eventName.length > MAX_EVENT_NAME_LENGTH) {
        newErrors[`event_${index}`] = `Event name must be ${MAX_EVENT_NAME_LENGTH} characters or fewer`;
      } else if (eventNames.has(normalizedEventName)) {
        newErrors[`event_${index}`] = 'Event name must be unique';
      }
      eventNames.add(normalizedEventName);

      if (!isValidOptionalCap(eventConfig.dailyConversionCap)) {
        newErrors[`event_daily_cap_${index}`] = 'Daily cap must be a non-negative whole number';
      }

      if (!isValidOptionalCap(eventConfig.monthlyConversionCap)) {
        newErrors[`event_monthly_cap_${index}`] = 'Monthly cap must be a non-negative whole number';
      }

    });

    const capFields: Array<[keyof OfferFormData, string]> = [
      ['dailyClickCap', 'Daily click cap'],
      ['monthlyClickCap', 'Monthly click cap'],
      ['dailyConversionCap', 'Daily conversion cap'],
      ['monthlyConversionCap', 'Monthly conversion cap']
    ];
    capFields.forEach(([field, label]) => {
      if (!isValidOptionalCap(formData[field] as string)) {
        newErrors[field] = `${label} must be a non-negative whole number`;
      }
    });

    if (formData.countries.length === 0) {
      newErrors.countries = 'At least one country must be selected';
    }

    if (!formData.deviceType) {
      newErrors.deviceType = 'Device type must be selected';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate < startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      focusFirstInvalidField();
      return;
    }

    try {
      setSubmitting(true);
      setErrors(prev => ({ ...prev, submit: '' }));
      const offerData = {
          name: formData.name,
          advertiser_id: parseInt(formData.advertiserId),
          goal_type: formData.goalType === 'event' ? formData.eventName : formData.goalType,
          category: formData.category,
          payout: parseFloat(formData.payout),
          payout_currency: formData.payoutCurrency,
          daily_click_cap: formData.dailyClickCap ? parseInt(formData.dailyClickCap) : null,
          monthly_click_cap: formData.monthlyClickCap ? parseInt(formData.monthlyClickCap) : null,
          daily_conversion_cap: formData.dailyConversionCap ? parseInt(formData.dailyConversionCap) : null,
          monthly_conversion_cap: formData.monthlyConversionCap ? parseInt(formData.monthlyConversionCap) : null,
          start_date: formData.startDate,
          end_date: formData.endDate,
          redirect_url_template: formData.redirectUrlTemplate,
          preview_url: formData.previewUrl || null,
          description: formData.description || null,
          restriction: formData.restriction || null,
          is_active: formData.isActive,
          is_test: formData.isTest,
          countries: formData.countries,
          os: formData.deviceType ? [formData.deviceType] : [],
          events: buildEventsPayload()
      };

      await apiService.createOffer(offerData);
      alert('Offer created successfully!');
      navigate('/offers');
    } catch (error: any) {
      console.error('Failed to create offer:', error);
      setErrors(prev => ({ ...prev, submit: error.message || 'Failed to create offer' }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/offers');
  };

  // Calculate tier-based payouts for display
  const calculateTierPayouts = () => {
    const payout = parseFloat(formData.payout) || 0;
    return {
      standard: (payout * 0.40).toFixed(2),   // 40% for standard tier
      premium: (payout * 0.60).toFixed(2),    // 60% for premium tier
      sensitive: (payout * 0.80).toFixed(2)   // 80% for sensitive tier
    };
  };

  const tierPayouts = calculateTierPayouts();

  return (
    <div className="offer-form-page">
      <div className="page-header">
        <h1>Add New Offer</h1>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="offer-form" aria-busy={submitting}>
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Offer Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter offer name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="advertiserId">Advertiser *</label>
                <select
                  id="advertiserId"
                  name="advertiserId"
                  value={formData.advertiserId}
                  onChange={handleInputChange}
                  className={errors.advertiserId ? 'error' : ''}
                  disabled={advertisersLoading}
                >
                  <option value="">{advertisersLoading ? 'Loading advertisers...' : 'Select Advertiser'}</option>
                  {advertisers.map(advertiser => (
                    <option key={advertiser.id} value={advertiser.id}>
                      {advertiser.name}
                    </option>
                  ))}
                </select>
                {errors.advertiserId && <span className="error-message">{errors.advertiserId}</span>}
                {errors.fetch && <span className="error-message" role="alert">{errors.fetch}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="goalType">Goal Type *</label>
                <select
                  id="goalType"
                  name="goalType"
                  value={formData.goalType}
                  onChange={handleInputChange}
                >
                  <option value="install">App Install</option>
                  <option value="lead">Lead Generation</option>
                  <option value="sale">Sale/Purchase</option>
                  <option value="event">Custom Event</option>
                </select>
              </div>

              {formData.goalType === 'event' && (
                <div className="form-group">
                  <label htmlFor="eventName">Event Name *</label>
                  <input
                    type="text"
                    id="eventName"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleInputChange}
                    placeholder="e.g., purchase, registration, subscription"
                    className={errors.eventName ? 'error' : ''}
                  />
                  {errors.eventName && <span className="error-message">{errors.eventName}</span>}
                  <small className="field-hint">Enter the custom event name for tracking</small>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="Non Incent">Non Incent</option>
                  <option value="Incent">Incent</option>
                </select>
                <small className="field-hint">Offer incentive category</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payout">Payout *</label>
                <div className="payout-group">
                  <input
                    type="number"
                    id="payout"
                    name="payout"
                    value={formData.payout}
                    onChange={handleInputChange}
                    className={errors.payout ? 'error' : ''}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  <select
                    name="payoutCurrency"
                    value={formData.payoutCurrency}
                    onChange={handleInputChange}
                    className="currency-select"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
                {errors.payout && <span className="error-message">{errors.payout}</span>}
              </div>
            </div>

            {/* Tier-based Payout Display */}
            {formData.payout && parseFloat(formData.payout) > 0 && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Publisher Payouts by Tier</label>
                  <div className="tier-payouts-display">
                    <div className="tier-payout-item">
                      <span className="tier-label">Standard Tier (40%):</span>
                      <span className="tier-amount">{formData.payoutCurrency} {tierPayouts.standard}</span>
                    </div>
                    <div className="tier-payout-item">
                      <span className="tier-label">Premium Tier (60%):</span>
                      <span className="tier-amount">{formData.payoutCurrency} {tierPayouts.premium}</span>
                    </div>
                    <div className="tier-payout-item">
                      <span className="tier-label">Sensitive Tier (80%):</span>
                      <span className="tier-amount">{formData.payoutCurrency} {tierPayouts.sensitive}</span>
                    </div>
                  </div>
                  <small className="field-hint">
                    Publishers receive different payouts based on their tier level
                  </small>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group full-width">
                <div className="event-config-header">
                  <div>
                    <label>Additional Events</label>
                    <small className="field-hint">
                      Primary payable event is {getPrimaryEventName() || 'install'}. Additional events are non-payable and publisher postbacks can be fired manually.
                    </small>
                  </div>
                  <button type="button" className="btn btn-secondary btn-compact" onClick={addEventConfig}>
                    Add Event
                  </button>
                </div>

                {eventConfigs.length > 0 && (
                  <div className="event-config-list">
                    {eventConfigs.map((eventConfig, index) => (
                      <div className="event-config-card" key={`event-config-${index}`}>
                        <div className="event-config-card-header">
                          <strong>Event {index + 2}</strong>
                          <button type="button" className="btn btn-secondary btn-compact" onClick={() => removeEventConfig(index)}>
                            Remove
                          </button>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Event Name *</label>
                            <input
                              type="text"
                              value={eventConfig.eventName}
                              onChange={(e) => updateEventConfig(index, 'eventName', e.target.value)}
                              className={errors[`event_${index}`] ? 'error' : ''}
                              placeholder="paid_upgrade"
                            />
                            {errors[`event_${index}`] && <span className="error-message">{errors[`event_${index}`]}</span>}
                          </div>

                          <div className="form-group">
                            <label>Payout</label>
                            <input
                              type="number"
                              value={eventConfig.payout}
                              onChange={(e) => updateEventConfig(index, 'payout', e.target.value)}
                              placeholder={formData.payout || '0.00'}
                              step="0.01"
                              min="0"
                              disabled
                            />
                            <small className="field-hint">Disabled for non-payable supporting events</small>
                          </div>

                          <div className="form-group">
                            <label>Revenue</label>
                            <input
                              type="number"
                              value={eventConfig.revenue}
                              onChange={(e) => updateEventConfig(index, 'revenue', e.target.value)}
                              placeholder={eventConfig.payout || formData.payout || '0.00'}
                              step="0.01"
                              min="0"
                              disabled
                            />
                            <small className="field-hint">Disabled for non-payable supporting events</small>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Daily Conversion Cap</label>
                            <input
                              type="number"
                              value={eventConfig.dailyConversionCap}
                              onChange={(e) => updateEventConfig(index, 'dailyConversionCap', e.target.value)}
                              className={errors[`event_daily_cap_${index}`] ? 'error' : ''}
                              placeholder="No limit"
                              min="0"
                              step="1"
                            />
                            {errors[`event_daily_cap_${index}`] && <span className="error-message">{errors[`event_daily_cap_${index}`]}</span>}
                          </div>

                          <div className="form-group">
                            <label>Monthly Conversion Cap</label>
                            <input
                              type="number"
                              value={eventConfig.monthlyConversionCap}
                              onChange={(e) => updateEventConfig(index, 'monthlyConversionCap', e.target.value)}
                              className={errors[`event_monthly_cap_${index}`] ? 'error' : ''}
                              placeholder="No limit"
                              min="0"
                              step="1"
                            />
                            {errors[`event_monthly_cap_${index}`] && <span className="error-message">{errors[`event_monthly_cap_${index}`]}</span>}
                          </div>
                        </div>

                        <div className="event-config-toggles">
                          <span className="event-config-note">Non-payable. Auto publisher postback is disabled; use manual fire if needed.</span>
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={eventConfig.isActive}
                              onChange={(e) => updateEventConfig(index, 'isActive', e.target.checked)}
                            />
                            Active
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dailyClickCap">Daily Click Cap</label>
                <input
                  type="number"
                  id="dailyClickCap"
                  name="dailyClickCap"
                  value={formData.dailyClickCap}
                  onChange={handleInputChange}
                  className={errors.dailyClickCap ? 'error' : ''}
                  placeholder="0 for unlimited"
                  min="0"
                  step="1"
                />
                {errors.dailyClickCap && <span className="error-message">{errors.dailyClickCap}</span>}
                <small className="field-hint">Maximum clicks per day (0 for unlimited)</small>
              </div>

              <div className="form-group">
                <label htmlFor="monthlyClickCap">Monthly Click Cap</label>
                <input
                  type="number"
                  id="monthlyClickCap"
                  name="monthlyClickCap"
                  value={formData.monthlyClickCap}
                  onChange={handleInputChange}
                  className={errors.monthlyClickCap ? 'error' : ''}
                  placeholder="0 for unlimited"
                  min="0"
                  step="1"
                />
                {errors.monthlyClickCap && <span className="error-message">{errors.monthlyClickCap}</span>}
                <small className="field-hint">Maximum clicks per month (0 for unlimited)</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dailyConversionCap">Daily Conversion Cap</label>
                <input
                  type="number"
                  id="dailyConversionCap"
                  name="dailyConversionCap"
                  value={formData.dailyConversionCap}
                  onChange={handleInputChange}
                  className={errors.dailyConversionCap ? 'error' : ''}
                  placeholder="0 for unlimited"
                  min="0"
                  step="1"
                />
                {errors.dailyConversionCap && <span className="error-message">{errors.dailyConversionCap}</span>}
                <small className="field-hint">Maximum conversions per day (0 for unlimited)</small>
              </div>

              <div className="form-group">
                <label htmlFor="monthlyConversionCap">Monthly Conversion Cap</label>
                <input
                  type="number"
                  id="monthlyConversionCap"
                  name="monthlyConversionCap"
                  value={formData.monthlyConversionCap}
                  onChange={handleInputChange}
                  className={errors.monthlyConversionCap ? 'error' : ''}
                  placeholder="0 for unlimited"
                  min="0"
                  step="1"
                />
                {errors.monthlyConversionCap && <span className="error-message">{errors.monthlyConversionCap}</span>}
                <small className="field-hint">Maximum conversions per month (0 for unlimited)</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={errors.startDate ? 'error' : ''}
                />
                {errors.startDate && <span className="error-message">{errors.startDate}</span>}
                <small className="field-hint">Offer will be active from this date</small>
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date *</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className={errors.endDate ? 'error' : ''}
                  min={formData.startDate}
                />
                {errors.endDate && <span className="error-message">{errors.endDate}</span>}
                <small className="field-hint">Offer will be paused after this date</small>
              </div>
            </div>

          </div>

          <div className="form-section">
            <h2>URLs & Tracking</h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="redirectUrlTemplate">Redirect URL Template *</label>
                <input
                  type="text"
                  id="redirectUrlTemplate"
                  name="redirectUrlTemplate"
                  value={formData.redirectUrlTemplate}
                  onChange={handleInputChange}
                  className={errors.redirectUrlTemplate ? 'error' : ''}
                  placeholder="https://track.example.com/click?offer_id={offer_id}&aff_id={aff_id}"
                />
                {errors.redirectUrlTemplate && <span className="error-message">{errors.redirectUrlTemplate}</span>}
                <small className="field-hint">
                  Use macros: {'{offer_id}'}, {'{aff_id}'}, {'{aff_sub}'}, {'{click_id}'}
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="previewUrl">Preview URL</label>
                <input
                  type="text"
                  id="previewUrl"
                  name="previewUrl"
                  value={formData.previewUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/app-preview"
                />
                <small className="field-hint">URL where publishers can preview the offer</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter offer description"
                  rows={3}
                />
                <small className="field-hint">Detailed description of the offer for publishers</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="restriction">Restrictions</label>
                <textarea
                  id="restriction"
                  name="restriction"
                  value={formData.restriction}
                  onChange={handleInputChange}
                  placeholder="Enter any restrictions or requirements for this offer"
                  rows={3}
                />
                <small className="field-hint">Any restrictions, requirements, or special notes for publishers</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Targeting</h2>

            <div className="form-row">
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Countries * (Multi-select)</label>
                <input
                  ref={countryInputRef}
                  type="text"
                  value={countrySearch}
                  onChange={handleCountrySearch}
                  onFocus={() => setShowCountryDropdown(true)}
                  className="form-input"
                  placeholder="Type to search and select countries..."
                  autoComplete="off"
                />

                {/* Selected Countries Display */}
                {formData.countries.length > 0 && (
                  <div className="selected-countries" style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {formData.countries.map(country => (
                      <span
                        key={country}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        {country}
                        <button
                          type="button"
                          onClick={() => handleRemoveCountry(country)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0',
                            marginLeft: '2px'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Country Dropdown */}
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div
                    ref={countryDropdownRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      backgroundColor: '#4a5568',
                      border: '1px solid #5a6c7d',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      marginTop: '4px'
                    }}
                  >
                    {/* Select All / Deselect All */}
                    <div
                      style={{
                        padding: '8px 12px',
                        borderBottom: '2px solid #5a6c7d',
                        backgroundColor: '#3c4855',
                        display: 'flex',
                        gap: '10px'
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleSelectAllCountries}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={handleDeselectAllCountries}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Deselect All
                      </button>
                    </div>
                    {filteredCountries.map((country) => (
                      <div
                        key={country}
                        onClick={() => handleCountryToggle(country)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #5a6c7d',
                          color: '#ecf0f1',
                          backgroundColor: formData.countries.includes(country) ? '#3498db' : 'transparent',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                          if (!formData.countries.includes(country)) {
                            e.currentTarget.style.backgroundColor = '#3c4855';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!formData.countries.includes(country)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.countries.includes(country)}
                          readOnly
                          style={{ marginRight: '4px' }}
                        />
                        {country}
                      </div>
                    ))}
                  </div>
                )}
                {errors.countries && <span className="error-message">{errors.countries}</span>}
                <small className="field-hint">Selected: {formData.countries.length} countries</small>
              </div>

              <div className="form-group">
                <label htmlFor="deviceType">Device Type *</label>
                <select
                  id="deviceType"
                  name="deviceType"
                  value={formData.deviceType}
                  onChange={handleInputChange}
                  className={errors.deviceType ? 'error' : ''}
                >
                  <option value="">Select Device Type</option>
                  {availableDeviceTypes.map(device => (
                    <option key={device} value={device}>
                      {device}
                    </option>
                  ))}
                </select>
                {errors.deviceType && <span className="error-message">{errors.deviceType}</span>}
                <small className="field-hint">Select the primary device type for this offer</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Status</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active (offer can receive traffic)
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="isTest"
                    checked={formData.isTest}
                    onChange={handleInputChange}
                  />
                  Test Offer (for testing purposes)
                </label>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="form-submit-error" role="alert">
              <strong>Offer could not be created.</strong>
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || advertisersLoading || Boolean(errors.fetch)}>
              {submitting ? 'Creating...' : 'Create Offer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOffer;
